import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { Text } from '@carouselgg/nexus/text'
import { Button } from '@carouselgg/nexus/button'
import { Callout } from '@carouselgg/nexus/callout'
import { UserCheck, UserX } from '@carouselgg/nexus/icon'
import { listMyTeamInvites, answerTeamInvite } from '../api/recruit'
import type { MyTeamInvite } from '../api/recruit'
import styles from './TeamInviteAnswer.module.css'

type State =
  | { kind: 'loading' }
  | { kind: 'open'; invite: MyTeamInvite }
  | { kind: 'closed' }
  | { kind: 'joined'; invite: MyTeamInvite }
  | { kind: 'declined' }

function deadlineText(expiresAt: string, now = new Date()): string {
  const at = new Date(expiresAt)
  const when = at.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
  const days = Math.floor((at.getTime() - now.getTime()) / 86_400_000)
  const left = days <= 0 ? 'under a day left' : days === 1 ? '1 day left' : `${days} days left`
  return `Answer by ${when} · ${left}`
}

/**
 * Answers a captain's team invite right from its notification: the deadline,
 * then Join or Decline. An invite already answered, or past its deadline,
 * isn't open any more.
 */
export function TeamInviteAnswer({ applicationId }: { applicationId: string }) {
  const [state, setState] = useState<State>({ kind: 'loading' })
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    listMyTeamInvites()
      .then(({ applications }) => {
        if (!live) return
        const invite = applications.find((a) => a.id === applicationId)
        setState(invite ? { kind: 'open', invite } : { kind: 'closed' })
      })
      .catch((err) => live && setProblem(err instanceof Error ? err.message : 'Couldn’t load the invite'))
    return () => { live = false }
  }, [applicationId])

  const answer = async (invite: MyTeamInvite, status: 'accepted' | 'declined') => {
    setBusy(true)
    setProblem(null)
    try {
      await answerTeamInvite(invite.id, status)
      setState(status === 'accepted' ? { kind: 'joined', invite } : { kind: 'declined' })
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Couldn’t answer the invite')
    } finally {
      setBusy(false)
    }
  }

  if (problem && state.kind === 'loading') return <Callout variant="error">{problem}</Callout>
  if (state.kind === 'loading') return null
  if (state.kind === 'closed') return <Text size="sm" muted>This invite isn't open any more — it was answered, or its time ran out.</Text>
  if (state.kind === 'declined') return <Text size="sm" muted>You declined. You're still in the free-agent pool.</Text>
  if (state.kind === 'joined') {
    const { invite } = state
    return (
      <Callout variant="success">
        You joined {invite.team_name}.{' '}
        {invite.org_slug && <Link to={`/org/${invite.org_slug}`}>Go to {invite.org_name ?? invite.team_name}</Link>}
      </Callout>
    )
  }

  const { invite } = state
  return (
    <div className={styles.answer}>
      {invite.expires_at && (
        <Text size="sm" weight="semibold" className={styles.deadline}>{deadlineText(invite.expires_at)}</Text>
      )}
      {invite.message && <blockquote className={styles.quote}>“{invite.message}”</blockquote>}
      {problem && <Callout variant="error">{problem}</Callout>}
      <div className={styles.buttons}>
        <Button variant="primary" size="sm" disabled={busy} onClick={() => answer(invite, 'accepted')}>
          <UserCheck size={14} /> Join {invite.team_name ?? 'the team'}
        </Button>
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => answer(invite, 'declined')}>
          <UserX size={14} /> Decline
        </Button>
      </div>
      <Text size="xs" muted>
        Joining puts you on the roster and takes you out of the free-agent pool.
        {invite.expires_at && ' If you don’t answer in time, the invite expires and you stay in the pool.'}
      </Text>
    </div>
  )
}
